/** 承認待ち（pending_approval / draft）の投稿カード */

import { useState } from 'react'
import type { QueueItem } from '../../lib/postTypes'
import { Link } from 'react-router-dom'
import { approveBlockReason, composeRetryHref, needsRecreate } from '../../lib/queueRules'
import { InlineError } from '../../components/QueryState'
import { useQueueActions, type Notify } from './useQueueActions'
import { PostBody } from './PostBody'
import { ChannelRows } from './ChannelRows'
import { ScheduleForm } from './ScheduleForm'
import { RejectButton } from './RejectButton'

export function ApprovalCard({ item, notify }: { item: QueueItem; notify: Notify }) {
  const { post, channels, activeVersion } = item
  const actions = useQueueActions(item, notify)
  const { approve, reject, edit, pending, error } = actions
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editText, setEditText] = useState('')
  const blockReason = approveBlockReason(post, channels)
  const approveDisabled = pending || !!blockReason

  return (
    <div className="gm-card p-4">
      <PostBody item={item}
        badge={post.status === 'draft'
          ? <span className="gm-chip gm-chip-warn shrink-0">要修正</span>
          : undefined} />
      <ChannelRows channels={channels} actions={actions} />
      <div className="mt-3 flex flex-wrap gap-2">
        <button disabled={approveDisabled} onClick={() => approve.mutate({})}
          title="承認してストックに入れます（今すぐ投稿・日時予約するまで投稿されません）"
          className="gm-btn gm-btn-secondary">承認（ストックへ）</button>
        <button disabled={!!blockReason} onClick={() => setScheduleOpen(o => !o)} aria-expanded={scheduleOpen}
          className="gm-btn gm-btn-secondary">日時を指定して予約</button>
        <button onClick={() => {
          if (editOpen) { setEditOpen(false); return }
          setEditOpen(true)
          setEditText(activeVersion?.variants.x?.text ?? '')
        }} aria-expanded={editOpen}
          className="gm-btn gm-btn-secondary">本文を編集</button>
        {/* 承認と同じ検査を通って即投稿（/publish は pending_approval を受け付けない — audit C2） */}
        <button disabled={approveDisabled} onClick={() => approve.mutate({ publishNow: true })}
          className="gm-btn gm-btn-secondary">今すぐ投稿</button>
        {needsRecreate(post) && (
          <Link to={composeRetryHref(post)} className="gm-btn gm-btn-secondary">作り直す</Link>
        )}
        <RejectButton disabled={pending} onConfirm={() => reject.mutate()} className="ml-auto" />
      </div>
      {blockReason && <p className="mt-2 text-sm text-warn">{blockReason}</p>}
      <InlineError message={error?.message} issues={error?.issues} className="mt-2" />
      {scheduleOpen && !blockReason && (
        <ScheduleForm disabled={pending}
          onSubmit={iso => approve.mutate({ scheduledAt: iso }, { onSuccess: () => setScheduleOpen(false) })} />
      )}
      {editOpen && (
        <div className="mt-2 space-y-2">
          <label htmlFor={`edit-${post.id}`} className="sr-only">本文</label>
          <textarea id={`edit-${post.id}`} value={editText} onChange={e => setEditText(e.target.value)} rows={4}
            className="gm-input h-auto py-2" />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              disabled={!editText.trim() || pending}
              onClick={() => edit.mutate(editText, { onSuccess: () => setEditOpen(false) })}
              className="gm-btn gm-btn-secondary">
              保存して再チェック
            </button>
            <span className="text-xs text-ink-muted">保存すると新しい版が作られ、安全チェックをやり直します（投稿不可からの復帰もここから）</span>
          </div>
        </div>
      )}
    </div>
  )
}
